import { Amplify } from "aws-amplify";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { sessionStorage as amplifySessionStorage } from "aws-amplify/utils";

import { runtimeEnv } from "./runtime-env";

/**
 * Cognito app-context for web-admin. The web-admin SPA is the admin app;
 * tokens minted by the admin Cognito client must carry the `admin` group,
 * otherwise the cross-app gate at `/v1/auth/session` rejects them.
 */
export const APP_CONTEXT = "admin" as const;
export const REQUIRED_GROUP = "admin" as const;
export const OPPOSITE_GROUP = "customer" as const;

export interface CognitoEnv {
  region: string;
  userPoolId: string;
  userPoolClientId: string;
}

function readEnv(): CognitoEnv {
  const region = runtimeEnv("VITE_COGNITO_REGION");
  const userPoolId = runtimeEnv("VITE_COGNITO_USER_POOL_ID");
  const userPoolClientId = runtimeEnv("VITE_COGNITO_CLIENT_ID");
  if (!region || !userPoolId || !userPoolClientId) {
    throw new Error(
      "Cognito config missing — set VITE_COGNITO_REGION, VITE_COGNITO_USER_POOL_ID, and VITE_COGNITO_CLIENT_ID for this deployment.",
    );
  }
  return { region, userPoolId, userPoolClientId };
}

let configured = false;

/**
 * Configure Amplify v6 once on app boot. Idempotent so HMR doesn't blow up.
 * Pool config is the SHARED Cognito user pool; web-admin passes the admin
 * client id (per ADR-0010 §3.3).
 */
export function configureCognito(): void {
  if (configured) return;
  const env = readEnv();
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: env.userPoolId,
        userPoolClientId: env.userPoolClientId,
        loginWith: { email: true },
      },
    },
  });
  // Cognito tokens stay in sessionStorage (not Amplify's localStorage default).
  //
  // Originally this mirrored the 2026-06-20 "session dies on browser close"
  // rule. That rule is superseded (ADR-0036) — the Penn session now lives in the
  // `penn_rt` cookie — but the override is DELIBERATELY kept: since the Penn
  // cookie carries the session, these tokens are load-bearing only for the
  // first exchange and the fallback re-exchange, and keeping them per-tab
  // leaves the smaller blast radius. Do not "fix" this to match the old
  // comment's reasoning.
  cognitoUserPoolsTokenProvider.setKeyValueStorage(amplifySessionStorage);
  configured = true;
}
